import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { DashboardQueueRow, EstimateSummary } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { DefinitionList, StatCard } from '../../components/ui/DefinitionList';
import { InfoCell } from '../../components/ui/InfoCell';
import { StatusText } from '../../components/ui/StatusText';
import { Timeline } from '../../components/ui/Timeline';
import { formatCount, formatDay, formatDayTime, formatKm, formatMoney } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { estimateStatusTone, vehicleTitle } from '../estimates/estimates-display';

const RECENT_LIMIT = 5;

/**
 * `/dashboard` (plan §5.7, §7; PP §3).
 *
 * Every figure on this page is a **SQL aggregate against the tenant's own business day**, and every one
 * of them opens the list it counts: a number a person cannot click is a number they have to go and look
 * for. The four counters are the workspace in one line — what is being quoted, what is waiting on a
 * customer, what is standing in the yard and what has been sold — and the three money cards under them
 * say how much of each total is still an estimate rather than pretending it is settled.
 *
 * "Needs attention" is the only part of the page with an opinion: four queues, oldest first, each one a
 * thing somebody has to do. The approvals queue is an administrator's; a salesperson simply does not see
 * that row, because the service returns it empty rather than refusing the card.
 */
export const DashboardPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const { isAdmin } = useAuth();
  const statsQuery = useQuery(orpc.dashboard.stats.queryOptions());
  const queuesQuery = useQuery(orpc.dashboard.queues.queryOptions());
  const recentQuery = useQuery(orpc.dashboard.recentEstimates.queryOptions({ input: { limit: RECENT_LIMIT } }));
  const healthQuery = useQuery(orpc.health.get.queryOptions());

  const columns = useMemo<DataTableColumns<EstimateSummary>>(
    () => [
      {
        id: 'estimateNo',
        header: t('estimates.columnEstimate'),
        accessorFn: (row) => row.estimateNo,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-medium text-foreground">{row.original.estimateNo}</p>
            <p className="truncate text-xs text-muted-foreground">{formatDay(row.original.updatedAt)}</p>
          </div>
        ),
      },
      {
        id: 'customer',
        header: t('estimates.columnCustomer'),
        accessorFn: (row) => row.customerName ?? '',
        cell: ({ row }) => (
          <InfoCell
            title={row.original.customerName ?? t('estimates.customerNotRecorded')}
            subline={row.original.customerPhone}
          />
        ),
      },
      {
        id: 'vehicle',
        header: t('estimates.columnVehicle'),
        enableSorting: false,
        cell: ({ row }) => (
          <InfoCell
            title={vehicleTitle(row.original) === '' ? t('common.notRecorded') : vehicleTitle(row.original)}
            subline={[row.original.modelYear ?? null, row.original.odometerKm === null ? null : formatKm(row.original.odometerKm)]
              .filter((part) => part !== null)
              .join(' · ')}
          />
        ),
      },
      {
        id: 'customerAskingPrice',
        header: t('estimates.columnAsking'),
        accessorFn: (row) => row.customerAskingPrice ?? 0,
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block tabular-nums text-foreground"
          >
            {formatMoney(row.original.customerAskingPrice)}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('estimates.columnStatus'),
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusText
            label={t(`estimates.statuses.${row.original.status}`)}
            tone={estimateStatusTone(row.original.status)}
          />
        ),
      },
    ],
    [t],
  );

  const stats = statsQuery.data;

  const queues = queuesQuery.data ?? null;

  const counter = (value: number | null | undefined, hint: string, unavailableHint: string, to?: string) => (
    <button
      type="button"
      disabled={to === undefined}
      onClick={() => (to === undefined ? undefined : navigate(to))}
      className="w-full text-start disabled:cursor-default"
    >
      <StatCard
        label={hint}
        value={
          value === null || value === undefined ? (
            t('common.notSet')
          ) : (
            <span
              dir="ltr"
              className="tabular-nums"
            >
              {value}
            </span>
          )
        }
        hint={value === null || value === undefined ? unavailableHint : undefined}
      />
    </button>
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/estimates/new')}>
          <Plus className="size-4" />
          {t('estimates.new')}
        </Button>
      </header>

      {statsQuery.isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {counter(stats?.openEstimates, t('dashboard.openEstimates'), '', '/estimates')}
        {counter(stats?.offersAwaitingResponse, t('dashboard.offersAwaiting'), '', '/estimates?status=OFFER_SHARED')}
        {counter(stats?.vehiclesInStock ?? null, t('dashboard.vehiclesInStock'), '', '/vehicles')}
        {counter(stats?.soldVehicles ?? null, t('dashboard.soldVehicles'), '', '/vehicles?status=SOLD')}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <MoneyCard
          label={t('dashboard.stockCost')}
          hint={t('dashboard.stockCostHint')}
          money={stats?.stockCost}
        />
        <MoneyCard
          label={t('dashboard.grossContribution')}
          hint={t('dashboard.grossContributionHint', { count: stats?.soldThisMonth ?? 0 })}
          money={stats?.grossContribution}
        />
        <section className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('dashboard.aging')}</p>
          <DefinitionList
            className="mt-1"
            items={(['d0_30', 'd31_60', 'd61_90', 'over90'] as const).map((bucket) => ({
              label: t(`dashboard.agingBuckets.${bucket}`),
              value: (
                <span
                  dir="ltr"
                  className="tabular-nums"
                >
                  {formatCount(stats?.aging[bucket] ?? 0)}
                </span>
              ),
            }))}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('dashboard.agingHint')}</p>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <section className="space-y-4 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-card-foreground">{t('dashboard.recentEstimates')}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/estimates')}
            >
              {t('dashboard.viewAll')}
            </Button>
          </div>
          <DataTable
            columns={columns}
            data={recentQuery.data ?? []}
            isLoading={recentQuery.isLoading}
            emptyMessage={t('estimates.empty')}
            pageSize={RECENT_LIMIT}
            onRowClick={(row) => navigate(`/estimates/${row.id}`)}
          />
        </section>

        <div className="space-y-4">
          <section className="space-y-4 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-card-foreground">{t('dashboard.needsAttention')}</h2>
            {queuesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('app.loading')}</p>
            ) : queues === null ? (
              <p className="text-sm text-muted-foreground">{t('errors.loadFailed')}</p>
            ) : queues.pendingApprovals.length + queues.staleOffers.length + queues.longInService.length + queues.unresolvedCostsOnSold.length ===
              0 ? (
              <p className="text-sm text-muted-foreground">{t('dashboard.nothingPending')}</p>
            ) : (
              <div className="space-y-4">
                {isAdmin ? (
                  <Queue
                    title={t('dashboard.queuePendingApprovals')}
                    tone="danger"
                    rows={queues.pendingApprovals}
                  />
                ) : null}
                <Queue
                  title={t('dashboard.queueStaleOffers')}
                  tone="warning"
                  rows={queues.staleOffers}
                />
                <Queue
                  title={t('dashboard.queueLongInService')}
                  tone="warning"
                  rows={queues.longInService}
                />
                <Queue
                  title={t('dashboard.queueUnresolvedCosts')}
                  tone="info"
                  rows={queues.unresolvedCostsOnSold}
                />
              </div>
            )}
          </section>

          <section className="space-y-4 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-card-foreground">{t('dashboard.flowTitle')}</h2>
            <Timeline
              items={[1, 2, 3, 4].map((stepNumber) => ({
                key: `flow-${stepNumber}`,
                title: t(`dashboard.flowStep${stepNumber}Title`),
                detail: t(`dashboard.flowStep${stepNumber}Body`),
              }))}
            />
          </section>

          <section className="space-y-2 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-card-foreground">{t('dashboard.healthTitle')}</h2>
            {healthQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('app.loading')}</p>
            ) : healthQuery.isError || !healthQuery.data ? (
              <StatusText
                label={t('dashboard.healthUnreachable')}
                tone="danger"
              />
            ) : (
              <>
                <StatusText
                  label={healthQuery.data.db ? t('dashboard.healthOk') : t('dashboard.healthDegraded')}
                  tone={healthQuery.data.db ? 'success' : 'warning'}
                />
                <p className="text-xs text-muted-foreground">{t('dashboard.healthChecked', { time: formatDayTime(healthQuery.data.time) })}</p>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

/** A money total with the number of records behind it that are still an estimate (PP §3). */
const MoneyCard = ({ label, hint, money }: { label: string; hint: string; money?: { amount: number; provisionalCount: number } }) => {
  const { t } = useI18nTranslation();

  return (
    <StatCard
      label={label}
      value={
        <span className="inline-flex items-center gap-2">
          <span
            dir="ltr"
            className="tabular-nums"
          >
            {formatMoney(money?.amount ?? null)}
          </span>
          {money && money.provisionalCount > 0 ? (
            <Badge tone="warning">{t('dashboard.provisionalCount', { count: money.provisionalCount })}</Badge>
          ) : null}
        </span>
      }
      hint={hint}
    />
  );
};

/**
 * One queue: a heading, and the records under it oldest first. Each row prints **since when**, because
 * "waiting" is only worth attention with a length attached, and links straight at the record.
 */
const Queue = ({ title, tone, rows }: { title: string; tone: 'danger' | 'warning' | 'info'; rows: DashboardQueueRow[] }) => {
  const { t } = useI18nTranslation();

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <StatusText
        label={`${title} · ${rows.length}`}
        tone={tone}
      />
      <ul className="space-y-1">
        {rows.map((row) => (
          <li key={row.id}>
            <Link
              to={row.link}
              className="flex items-baseline justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
            >
              <span className="min-w-0 truncate text-foreground">{row.label}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{t('dashboard.since', { date: formatDay(row.since) })}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
